import { MapPinLineIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import { LocationPicker } from "@/features/search/components/LocationPicker";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { DestinationSearch } from "../components/DestinationSearch/DestinationSearch";
import type { HotelSearchForm } from "../HotelSearchForm";

type HotelDestinationPickerProps = {
  form: HotelSearchForm;
  analyticsSource: SearchSource;
  showErrorsInline?: boolean;
  onChange?: (values: HotelSearchFormInputValues) => void;
};

export function HotelDestinationPicker({
  form,
  analyticsSource,
  showErrorsInline,
  onChange,
}: HotelDestinationPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const destination = useWatch({ control: form.control, name: "destination" });

  return (
    <>
      <LocationPicker
        startIcon={<MapPinLineIcon />}
        label={intl.formatMessage({
          defaultMessage: "Destination",
          description: "hotels.search.destination",
        })}
        value={destination}
        shouldShowCode={false}
        onPickerOpen={() => {
          rudderAnalytics.track(
            HotelSearchTrackingEvents.USER_CLICKED_HOTEL_DESTINATION_INPUT,
            {
              source: analyticsSource,
            },
          );

          setIsDrawerOpen(true);
        }}
        errorMessage={
          showErrorsInline
            ? form.formState.errors.destination?.name?.message
            : undefined
        }
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
        heightOffset={24}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <DrawerTopBar
          onCloseClick={() => setIsDrawerOpen(false)}
          title={intl.formatMessage({
            defaultMessage: "Select a destination",
            description: "hotels.search.destinationSelect",
          })}
        />

        <DestinationSearch
          onChange={(value) => {
            form.setValue("destination", value);
            onChange?.(form.getValues());
            setIsDrawerOpen(false);
          }}
          analyticsSource={analyticsSource}
        />
      </SwipeableDrawer>
    </>
  );
}
